import {
  ArrowLeftOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  StopOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import {
  Tag as AntTag,
  Tooltip as AntTooltip,
  Button,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Row,
  Spin,
  Typography,
} from 'antd';
import { postImprimirOrdenDePago } from 'api/cuentaunicasir/index';
import { venta } from 'api/netpay';
import { getCargos } from 'api/recaudacion/cargos';
import {
  getConvenio,
  getParcialidades,
  postCancelarConvenio,
  postCancelarParcialidades,
} from 'api/recaudacion/convenios';
import { getRecibosPorParcialidades, postPagarConvenio } from 'api/recaudacion/recibos';
import { postObtenerReporte } from 'api/reporteador';
import FormDatosDePago, { fromCents, toCents } from 'components/FormDatosDePago';
import FormSubmitControls from 'components/FormSubmitCustomControls';
import NumericInput from 'components/NumericInput';
import Previewer, { downloadBase64 } from 'components/Previewer';
import Select from 'components/Select';
import Table from 'components/Table';
import useCorte from 'hooks/useCorte';
import Moment from 'moment';
import propTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { store } from 'store/index';
import {
  SET_LAST_PAYMENT_DATA_CONVENIO,
  SET_PADRON_PAGO_CONVENIO,
  SET_RECIBO_CONVENIO,
  SET_RECIBOS,
} from 'store/reducers/caja';
import styled from 'styled-components';
import {
  DATE_FORMAT,
  DATE_FRONTEND_FORMAT,
  DATE_NETPAY_FORMAT,
  formatReceived,
} from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { toMoment } from 'utils/normalizers';
import padronToString from 'utils/padronesToString';
import round from 'utils/round';
import currencyFormatter from 'utils/toCurrency';
import TableParcialidadesSaldadas from './TableParcialidadesSaldadas';

const { Text } = Typography;

const styleButton = {
  margin: '10px 0',
  padding: '10px 0',
  height: '-webkit-fill-available',
  width: '100%',
  borderRadius: 10,
  fontSize: 16,
  textWrap: 'pretty',
};

const parcialidadesMapper = (p) => ({
  ...p,
  sobrante: 0,
  _importe_de_la_parcialidad: -toCents(p.importe_de_la_parcialidad),
  importe_de_la_parcialidad: toCents(p.importe_de_la_parcialidad),
  adeudo: toCents(p.adeudo),
  importe_original: toCents(p.importe_original),
});

const getNoSaldados = (arr = []) => arr.filter((item) => !item.saldado);

const getNextMensualidad = (_data) => {
  const parcialidadesSinSaldar = getNoSaldados(_data).map((p) => p.mensualidad);
  const ultimaMensualidadSinPagar = Math.min(...parcialidadesSinSaldar);
  const first = _data.find((item) => item.mensualidad === ultimaMensualidadSinPagar);
  return first;
};

const PayButton = ({
  notAllowedReason,
  alerta,
  disabled,
  setVisibleCobroForm,
}) => {
  const stylesButton = {
    margin: '10px 0',
    height: '-webkit-fill-available',
    width: '100%',
  };

  const stylesCol = {
    display: 'flex',
    gap: 10,
    justifyContent: 'center',
    width: '-webkit-fill-available',
  };

  return (
    <Col xs={24} sm={24} md={8} lg={6}>
      {disabled ? (
        <Tooltip
          style={{ width: '-webkit-fill-available' }}
          title={!!notAllowedReason.length && (
            <>
              No puedes cobrar por las siguientes razones:
              {notAllowedReason.map((reason) => (
                <p key={reason}>{reason}</p>
              ))}
            </>
          )}
        >
          <Button
            id="pay-button"
            style={stylesButton}
            disabled
          >
            <Row>
              <Col
                span={24}
                style={stylesCol}
              >
                <DollarCircleOutlined />
                Pagar
              </Col>
              <Col span={24}>
                {alerta}
              </Col>
            </Row>
          </Button>
        </Tooltip>
      ) : (
        <Button
          id="pay-button"
          style={stylesButton}
          onClick={(e) => {
            e.preventDefault();
            setVisibleCobroForm(true);
          }}
        >
          <Row>
            <Col
              span={24}
              style={stylesCol}
            >
              <DollarCircleOutlined />
              Pagar
            </Col>
          </Row>
        </Button>
      )}
    </Col>
  );
};

PayButton.propTypes = {
  notAllowedReason: propTypes.arrayOf(propTypes.string).isRequired,
  alerta: propTypes.node.isRequired,
  setVisibleCobroForm: propTypes.func.isRequired,
  disabled: propTypes.bool.isRequired,
};

const Tooltip = styled(AntTooltip)`
  width: -webkit-fill-available;
`;

// eslint-disable-next-line react/prop-types
const ModalCobroParcialidades = ({ callback, permission }) => {
  const onChangingInput = useRef(false);
  const { visibleModalCorte, corte, alerta } = useCorte();
  const [form] = Form.useForm();
  const [formCancel] = Form.useForm();
  const [formCancelParcialidades] = Form.useForm();
  const dispatch = useDispatch();
  const {
    padronPagoConvenio: padron,
    ciudadano,
    netpayResponse,
  } = useSelector(({ caja }) => caja);
  const { user } = useSelector(({ auth }) => auth);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [convenio, setConvenio] = useState({ numero_de_pagos: 10 });
  const [visibleCobroForm, setVisibleCobroForm] = useState(false);
  const [datosDePago, setDatosDePago] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [tipoDeConvenio, setTipoDeConvenio] = useState(null);

  const [visibleCancel, setVisibleCancel] = useState(false);
  const fullFileName = `${Moment().format('YYYYMMDD HH:mm:ss')} - SIR - Convenio - ${padronToString(padron, padron?.tipo_de_padron)}.pdf`;

  const [descriptionItems, setDescriptionItems] = useState([]);
  const [visibleCancelParcialidades, setVisibleCancelParcialidades] = useState(false);
  const [notAllowedReason, setNotAllowedReason] = useState([]);
  const [base64, setBase64] = useState('');
  const [visiblePreview, setVisiblePreview] = useState(false);

  const [visibleGetRecibos, setVisibleGetRecibos] = useState(false);
  const [loadingNetPay, setLoadingNetPay] = useState(false);

  const fromInput = useRef(false);
  const fromTable = useRef(false);
  const netpayRef = useRef();

  const clearSobrante = () => {
    const cloneData = [...data];
    cloneData.forEach((item) => {
      // eslint-disable-next-line no-param-reassign
      item.sobrante = 0;
    });
    setData(cloneData);
  };

  const onCancel = (ignore = false) => {
    onChangingInput.current = false;
    if (visiblePreview) {
      setVisiblePreview(false);
      setBase64('');
      return;
    }
    if (visibleCancel) {
      setVisibleCancel(false);
      formCancel.resetFields();
      return;
    }
    if (visibleCobroForm) {
      setVisibleCobroForm(false);
      setDatosDePago([]);
      return;
    }
    if (visibleCancelParcialidades) {
      setVisibleCancelParcialidades(false);
      formCancelParcialidades.resetFields();
      return;
    }
    if (!visibleCobroForm || ignore) {
      setNotAllowedReason([]);
      setTipoDeConvenio(null);
      setData([]);
      setSelectedRowKeys([]);
      setVisibleCobroForm(false);
      dispatch({ type: SET_PADRON_PAGO_CONVENIO });
    }
  };

  const fillInputAbono = (_data = data, keys = selectedRowKeys) => {
    const _parcialidades = _data.filter((item) => keys.includes(item.id));
    const abono = _parcialidades.reduce((acc, curr) => acc + curr.adeudo, 0);

    setTimeout(() => {
      const shouldUpdateForm = fromTable.current && !fromInput.current;
      const shouldClearSobrante = tipoDeConvenio?.aplicar_parcialidades_completas
        && shouldUpdateForm;

      if (shouldUpdateForm) {
        form.setFieldsValue({ abono: fromCents(abono) });
      }

      if (shouldClearSobrante) {
        setData(_data.map((p) => ({ ...p, sobrante: 0 })));
      }

      fromTable.current = false;
      fromInput.current = false;
    }, 300);
  };

  const fetchAll = async () => {
    const [
      _data,
      _convenio,
      _cargos,
    ] = await Promise.all([
      getParcialidades({ convenio: padron.convenio }),
      getConvenio(padron.convenio),
      getCargos({ convenios: padron.convenio }, false),
    ]);
    return [_data, _convenio, _cargos];
  };

  // region Fetch data
  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      setLoading(true);
      const [
        _data,
        _convenio,
        _cargos,
      ] = await fetchAll();
      if (mounted) {
        setVisibleCancel(false);
        const mappedData = _data.map(parcialidadesMapper);
        if (mappedData.length && mappedData.some((e) => !e.saldado)) {
          const first = getNextMensualidad(mappedData);
          setSelectedRowKeys([first.id]);
          setTimeout(() => form.setFieldsValue({ abono: fromCents(first?.adeudo) || '0' }));
        }
        const formattedConvenio = formatReceived(_convenio);

        setData(mappedData);
        setConvenio(formattedConvenio);
        setTipoDeConvenio(_convenio?.tipo_de_convenio);
        setCargos(_cargos.map(({ id }) => id));
        const _notAllowedReason = [];
        if (!permission.cobrar) {
          _notAllowedReason.push('No tienes permisos para cobrar.');
        }
        if (!permission.pagar_en_cortes_anteriores) {
          _notAllowedReason.push('No tienes permiso para pagar en cortes anteriores.');
        }

        if (_notAllowedReason.length) {
          setNotAllowedReason(_notAllowedReason);
        }

        if (!_data.some((item) => item.saldado)) {
          delete formattedConvenio.fecha_ultimo_pago;
        }
        setVisibleCancel(false);

        // Calcular importes usando centavos para evitar errores de precisión
        const importeTotalCents = toCents(formattedConvenio.total_convenido_de_adeudos)
          - toCents(formattedConvenio.total_convenido_de_descuentos);
        const importeTotal = fromCents(importeTotalCents);

        const importePagado = formattedConvenio.pagado;

        const importeAdeudadoCents = toCents(importeTotal) - toCents(importePagado);
        const importeAdeudado = fromCents(importeAdeudadoCents);

        setDescriptionItems([
          {
            key: 'id',
            label: formattedConvenio?.folio ? 'Folio' : 'Número de convenio',
            children: formattedConvenio?.folio || formattedConvenio.id,
          },
          {
            key: 'fecha_de_creacion',
            label: 'Fecha de alta',
            children: formattedConvenio.fecha_de_creacion?.format(DATE_FRONTEND_FORMAT) || '.',
          },
          {
            key: 'fecha_ultimo_pago',
            label: 'Fecha de último pago',
            children: formattedConvenio.fecha_ultimo_pago?.format(DATE_FRONTEND_FORMAT) || '.',
          },
          {
            key: 'total',
            label: 'Importe total',
            children: currencyFormatter(importeTotal),
          },
          {
            key: 'numero_de_pagos',
            label: 'Número de pagos',
            children: formattedConvenio.numero_de_pagos,
          },
          {
            key: 'pagado',
            label: 'Importe pagado',
            children: currencyFormatter(importePagado),
          },
          {
            key: 'pagos_vencidos',
            label: 'Pagos vencidos',
            children: formattedConvenio.pagos_vencidos || 0,
          },
          {
            key: 'adeudo',
            label: 'Importe adeudado',
            children: currencyFormatter(importeAdeudado),
          },
          {
            key: 'observaciones',
            label: 'Observaciones',
            children: formattedConvenio.observaciones,
          },
        ]);
        setLoading(false);
      }
    };
    if (padron) {
      fetchData();
    }
    return () => { mounted = false; };
  }, [padron]);

  useEffect(() => {
    fillInputAbono(data);
  }, [selectedRowKeys]);

  useEffect(() => {
    if (visibleModalCorte) {
      onCancel(true);
    }
  }, [visibleModalCorte]);

  const netpayFeedback = ({ status_code, message: netPayMessage }) => {
    const error = {
      '00': 'success',
      '02': 'warn',
      '05': 'error',
    };
    const type = error[status_code] ?? 'info';
    message[type](netPayMessage, 5);
  };

  useEffect(() => {
    if (netpayResponse) {
      netpayFeedback(netpayResponse);
      // dispatch({ type: CLEAR_NETPAY_RESPONSE });
      dispatch({ type: SET_PADRON_PAGO_CONVENIO });
      dispatch({
        type: SET_RECIBO_CONVENIO,
        payload: netpayResponse.recibos.map((recibo) => ({ id: recibo.id })),
      });
      setLoadingNetPay(false);
      clearTimeout(netpayRef.current);
      netpayRef.current = null;
      setVisibleGetRecibos(false);
      setVisibleCobroForm(false);
      setSelectedRowKeys([]);
    }
    // eslint-disable-next-line
  }, [netpayResponse]);

  if (!padron) {
    return null;
  }

  const cols = [
    {
      titleText: 'Mensualidad',
      dataIndex: 'mensualidad',
      key: 'mensualidad',
      width: 140,
      align: 'center',
      render: (value, record) => <MensualidadRenderer value={value} record={record} />,
    },
    {
      titleText: 'Fecha de pago',
      dataIndex: 'fecha_de_pago',
      key: 'fecha_de_pago',
      width: 140,
      align: 'center',
      render: (value) => <FechaRenderer value={value} showTooltip />,
    },
    {
      titleText: 'Fecha de vencimiento',
      dataIndex: 'fecha_de_vencimiento',
      key: 'fecha_de_vencimiento',
      width: 180,
      align: 'center',
      render: (value, record) => <FechaVencimientoRenderer value={value} record={record} />,
    },
    {
      titleText: 'Importe',
      dataIndex: 'adeudo',
      key: 'adeudo',
      align: 'right',
      width: 200,
      render: (value, record) => <ImporteRenderer value={value} record={record} />,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    onChange: (keys) => {
      if (!corte) {
        message.warn(alerta);
        return;
      }
      const _selectedKeys = [...selectedRowKeys];
      const _data = getNoSaldados(data);
      const _filtered = _data.filter((item) => keys.includes(item.id)).map((p) => p.id);

      const _keys = _selectedKeys.length !== _filtered.length
        ? _filtered : [_data.map((p) => p.id)[0]];

      setSelectedRowKeys(_keys);
    },
    renderCell: (checked, record) => (
      <Checkbox
        checked={checked}
        disabled={record.saldado}
      />
    ),
  };

  const handleOnRowClick = (record) => {
    if (!corte) {
      message.warn(alerta);
      return;
    }
    clearSobrante();
    fromTable.current = true;
    if (!record.saldado) {
      const prev = data.filter((p) => p.mensualidad < record.mensualidad && !p.saldado)
        .map((p) => p.id);
      if (selectedRowKeys.includes(record.id)) {
        const clone = [...selectedRowKeys];
        clone.splice(selectedRowKeys.indexOf(record.id), 1);
        setSelectedRowKeys([...prev]);
      } else {
        setSelectedRowKeys([...prev, record.id]);
      }
    }
  };

  const payWithNetPayConvenio = async () => {
    setLoadingNetPay(true);
    const values = {
      metodos_de_pago: [{
        metodo: 24, // NetPay
        importe: round(form.getFieldValue('abono')),
      }],
      ciudadano: ciudadano.id,
      padrones: [{
        padron_id: padron.id,
        tipo_de_padron: padron.tipo_de_padron,
        parcialidades: selectedRowKeys,
        cargos,
        importe: round(form.getFieldValue('abono')),
        importe_sin_redondeo: parseFloat(form.getFieldValue('abono')),
        convenio: convenio.id,
      }],
      convenio: convenio.id,
    };

    dispatch({
      type: SET_LAST_PAYMENT_DATA_CONVENIO,
      payload: { parcialidades: selectedRowKeys },
    });

    // Asi se debe mandar el traceability para convenios
    const response = await venta({
      serialNumber: corte?.terminal_de_netpay?.serial,
      folioNumber: new Date().toISOString(),
      amount: form.getFieldValue('abono'),
      traceability: {
        padrones: values.padrones.map((p) => ({
          ...p,
        })),
        es_convenio: true,
        usuario: user.id,
        convenio: convenio.id,
        parcialidades: values.padrones[0].parcialidades.join(),
        ciudadano: ciudadano.id,
        importe: form.getFieldValue('abono'),
      },
    });

    if (response) {
      netpayRef.current = setTimeout(() => setVisibleGetRecibos(true), 60000);
    } else {
      setLoadingNetPay(false);
    }
  };

  const onPayment = async (dataPagos) => {
    setLoading(true);
    const values = {
      metodos_de_pago: dataPagos.map((item) => ({
        ...item,
        importe: round(item.importe),
      })),
      es_convenio: true,
      ciudadano: ciudadano.id,
      padrones: [{
        padron_id: padron.id,
        tipo_de_padron: padron.tipo_de_padron,
        parcialidades: selectedRowKeys,
        cargos,
        importe: round(form.getFieldValue('abono')),
        importe_sin_redondeo: parseFloat(form.getFieldValue('abono')),
        convenio: convenio.id,
      }],
      convenio: convenio.id,
    };
    const recibos = await postPagarConvenio(values, padron);
    setLoading(false);
    if (recibos.success) {
      setVisibleCobroForm(false);
      dispatch({ type: SET_PADRON_PAGO_CONVENIO });
      setSelectedRowKeys([]);
    } else {
      message.warn('Hubo un error al procesar el pago, vuelva a intentarlo');
    }
  };

  const onChangeImporte = (e) => {
    onChangingInput.current = true;
    fromInput.current = true;
    clearSobrante();
    const { value } = e.target;
    const acumulado = [];
    const cloneData = [...data];

    if (value) {
      const _data = data.filter((item) => !item.saldado);
      const val = parseFloat(value);
      let resta = val;

      while (resta > 0) {
        const first = getNextMensualidad(_data);
        if (!first) {
          break;
        }
        const idx = _data.indexOf(first);
        const match = _data[idx];
        acumulado.push(match.id);

        // Simplificar: resta -= first.adeudo es más claro que resta += -first.adeudo
        resta -= first.adeudo;

        // Calcular sobrante de forma más clara
        const sobrante = resta < 0 ? Math.abs(resta) : 0;
        _data[idx].sobrante = sobrante;
        _data.splice(idx, 1);
      }
    } else {
      clearSobrante();
    }

    setTimeout(() => {
      setSelectedRowKeys(acumulado);
      setData(cloneData);
      onChangingInput.current = false;
    });
  };

  const getBase64Comprobante = async () => {
    setLoading(true);
    const values = {
      ID: tipoDeConvenio.reportes_de_convenio_id,
      Parametros: [
        {
          Nombre: 'convenio',
          TipoParametro: 'Entero',
          Valor: convenio.id,
        },
      ],
    };
    const _base64 = await postObtenerReporte(values);
    if (_base64 !== '') {
      downloadBase64(_base64, fullFileName);
    } else {
      message.warn('No se pudo obtener el comprobante');
    }
    setLoading(false);
  };

  const getLabelModal = () => {
    if (visibleCancel) return 'Cancelar convenio';
    if (visibleCobroForm) return 'Cobro';
    if (visibleCancelParcialidades) return 'Cancelar parcialidades';
    if (visiblePreview) return 'Orden de pago';
    return 'Información del convenio';
  };

  const getWidthModal = () => {
    if (visibleCobroForm) return 455;
    if (visibleCancel) return 490;
    return '80%';
  };

  const afterCancel = () => {
    setTimeout(() => {
      setVisibleCobroForm(false);
      setSelectedRowKeys([]);
      formCancel.resetFields();
      dispatch({ type: SET_PADRON_PAGO_CONVENIO });
    });
  };

  const wait = (s) => new Promise((resolve) => setTimeout(resolve, s * 1000));

  const getReciboNetPayConvenio = async () => {
    setLoading(true);

    const { lastPaymentDataConvenio } = store.getState().caja;
    const { recibos, error } = await getRecibosPorParcialidades(lastPaymentDataConvenio);
    if (recibos?.length) {
      message.info('Recibos encontrados');
      clearTimeout(netpayRef.current);
      netpayRef.current = null;
      setLoadingNetPay(false);
      setVisibleCobroForm(false);
      dispatch({ type: SET_PADRON_PAGO_CONVENIO });
      dispatch({ type: SET_RECIBOS, payload: recibos });
      setSelectedRowKeys([]);
    } else if (error) {
      message.warn(error);
      setLoadingNetPay(false);
      setLoading(false);
      setVisibleCobroForm(false);
    }
    setVisibleGetRecibos(false);
  };

  const handleCancelConvenio = async () => {
    try {
      setLoading(true);
      await formCancel.validateFields();
      const values = formCancel.getFieldsValue();
      const canceled = postCancelarConvenio(convenio.id, values);
      if (canceled) {
        // Este wait es para que se alcance a actualizar el estado del convenio
        // antes de que se haga el fetch de los padrones y el back-end me regrese el
        // cargo sin en convenio
        await wait(3);
        callback(padron.key);
        afterCancel();
      } else {
        message.warn('Hubo un error al cancelar el convenio, vuelva a intentarlo');
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleCancelParcialidades = async (values) => {
    try {
      setLoading(true);
      const canceled = await postCancelarParcialidades(values);
      if (canceled) {
        formCancelParcialidades.resetFields();
        const [_data, _convenio, _cargos] = await fetchAll();
        setData(_data.map(parcialidadesMapper));
        setCargos(_cargos.map(({ id }) => id));
        setConvenio(formatReceived(_convenio));
        setVisibleCancelParcialidades(false);
        if (_data.length && _data.some((e) => !e.saldado)) {
          const first = getNextMensualidad(_data);
          setSelectedRowKeys([first.id]);
          setTimeout(() => form.setFieldsValue({ abono: fromCents(first?.adeudo) || '0' }));
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickCanclarParcialidades = () => {
    setVisibleCancelParcialidades(true);
  };

  const handleGenerarOrdenDePago = async (referencia_normal = true) => {
    setLoading(true);
    const amount = form.getFieldValue('abono');
    const lastDayInstance = toMoment(new Date()).endOf('month');
    const fechaBilling = lastDayInstance.format(DATE_FORMAT);
    const fecha = lastDayInstance.format(DATE_NETPAY_FORMAT);
    const _cargos = padron.cargos
      .filter((cargo) => cargo.convenios.id === convenio.id)
      .map((cargo) => cargo.id);
    const result = await postImprimirOrdenDePago(
      amount,
      fecha,
      fechaBilling,
      _cargos,
      padron.id,
      padron.tipo_de_padron,
      ciudadano.id,
      referencia_normal,
      {},
      convenio.id,
    );
    if (result.success) {
      setBase64(result.base64);
      setVisiblePreview(true);
      setVisibleCobroForm(false);
    } else {
      message.warn('No se pudo obtener la orden de pago');
    }
    setLoading(false);
  };

  const recuperarRecibosTitle = (
    <Typography.Text type="warning">
      Solo usar en caso de finalizar la transacción
      (mensaje &quot;Recibo generado correctamente&quot; visible en terminal)
      y no haber sido redirigido al recibo.
    </Typography.Text>
  );

  const NetPayLoading = () => (
    <Row gutter={[10, 10]}>
      <Col span={24}>
        Esperando respuesta de terminal
      </Col>
      {visibleGetRecibos && (
        <Col span={24}>
          <Button onClick={getReciboNetPayConvenio}>
            <Tooltip title={recuperarRecibosTitle}>
              Recuperar recibos
              {' '}
              <QuestionCircleOutlined />
            </Tooltip>
          </Button>
        </Col>
      )}
      <Col span={24}>
        <Button onClick={() => {
          setLoading(false);
          setLoadingNetPay(false);
          clearTimeout(netpayRef.current);
          netpayRef.current = null;
          setVisibleGetRecibos(false);
        }}
        >
          Cancelar
        </Button>
      </Col>
    </Row>
  );

  return (
    <Modal
      open={!!padron}
      closable={false}
      footer={null}
      width={getWidthModal()}
      title={(
        <FormSubmitControls
          label={getLabelModal()}
          loading={loading || loadingNetPay}
          controls={[
            {
              onClick: onCancel,
              text: 'Regresar',
              icon: <ArrowLeftOutlined />,
            },
            visibleCancel && {
              onClick: handleCancelConvenio,
              text: 'Cancelar',
              icon: <StopOutlined />,
            },
            visibleCancelParcialidades && {
              onClick: () => formCancelParcialidades.submit(),
              text: 'Cancelar',
              icon: <StopOutlined />,
            },
          ].filter(Boolean)}
        />
      )}
      forceRender
      centered
    >
      <Spin
        spinning={loading || loadingNetPay}
        tip={loadingNetPay ? <NetPayLoading /> : 'Cargando...'}
      >
        {!visibleCobroForm && !visibleCancel && !visibleCancelParcialidades && !visiblePreview && (
          <>
            <Form
              layout="vertical"
              form={form}
              style={{ marginBottom: 14 }}
              onFinish={() => { setVisibleCobroForm(true); }}
            >
              <Descriptions
                size="small"
                bordered
                style={{ marginBottom: 10 }}
                column={{
                  lg: 2,
                  md: 2,
                  sm: 1,
                  xs: 1,
                }}
              >
                {descriptionItems.map((item) => (
                  <Descriptions.Item
                    key={item.key}
                    label={(
                      <Text>
                        {item.label}
                      </Text>
                    )}
                    span={item.key === 'observaciones' ? 2 : 1}
                  >
                    {item.children}
                  </Descriptions.Item>
                ))}
              </Descriptions>
              <Row gutter={10} justify="end">
                <Col xs={24} sm={24} md={6}>
                  <NumericInput
                    label="Importe a abonar"
                    name="abono"
                    required
                    decimal
                    currency
                    allowZero={false}
                    disabled={loading || !corte || tipoDeConvenio?.aplicar_parcialidades_completas}
                    onChange={onChangeImporte}
                    className="input-importe"
                    tooltip={tipoDeConvenio?.aplicar_parcialidades_completas
                      ? 'Debido a la configuración del tipo de convenio, no es posible pagar parcialidades incompletas.'
                      : null}
                  />
                </Col>
              </Row>
              {/* region Controls */}
              <Row gutter={10} justify="end">
                <Col xs={24} sm={24} md={8} lg={6}>
                  <Button
                    style={styleButton}
                    icon={<DownloadOutlined />}
                    onClick={() => message.warn('Se está trabajando esta funcionalidad.')}
                  >
                    Descargar auxiliar
                  </Button>
                </Col>
                <Col xs={24} sm={24} md={8} lg={6}>
                  <Button
                    style={styleButton}
                    icon={<DownloadOutlined />}
                    onClick={getBase64Comprobante}
                  >
                    Descargar convenio
                  </Button>
                </Col>
                {permission?.cancelar_convenio && (
                <Col xs={24} sm={24} md={8} lg={6}>
                  <Button
                    style={styleButton}
                    icon={<StopOutlined />}
                    onClick={onClickCanclarParcialidades}
                    disabled={!corte}
                    className="danger"
                  >
                    Cancelar parcialidades
                  </Button>
                </Col>
                )}
                <Col xs={24} sm={24} md={8} lg={6}>
                  <Button
                    disabled={loading || !corte}
                    icon={<StopOutlined />}
                    type="danger"
                    onClick={() => {
                      setVisibleCancel(true);
                    }}
                    style={styleButton}
                  >
                    Cancelar convenio
                  </Button>
                </Col>
                {/* 24010130165 */}
                <Col xs={24} sm={24} md={8} lg={6}>
                  <Button
                    disabled
                    icon={<SwapOutlined />}
                    style={styleButton}
                    onClick={() => {
                      if (selectedRowKeys.length === 0) {
                        message.warn('Seleccione al menos una parcialidad.');
                        return;
                      }
                      handleGenerarOrdenDePago(false);
                    }}
                  >
                    Transferencia
                  </Button>
                </Col>
                <Col xs={24} sm={24} md={8} lg={6}>
                  <Button
                    disabled
                    icon={<FileTextOutlined />}
                    style={styleButton}
                    onClick={() => {
                      if (selectedRowKeys.length === 0) {
                        message.warn('Seleccione al menos una parcialidad.');
                        return;
                      }
                      handleGenerarOrdenDePago();
                    }}
                  >
                    Orden de pago
                  </Button>
                </Col>
                <PayButton
                  notAllowedReason={notAllowedReason}
                  alerta={alerta}
                  disabled={loading || !selectedRowKeys.length || !corte}
                  setVisibleCobroForm={setVisibleCobroForm}
                />
              </Row>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
            <Col span={24}>
              {data.some((item) => item.saldado) && (
                <Text className="success">
                  *Las parcialidades mostradas de la siguiente forma
                  {' '}
                  <span className="txtdec-line-throught">$1000</span>
                  {' '}
                  significa que ya fue saldada o abonada*
                </Text>
              )}
              <Table
                cols={cols}
                rowSelection={rowSelection}
                data={data}
                handleOnRowClick={handleOnRowClick}
                rowKey="id"
                allowSort={false}
                allowSearch={false}
                mobileColIndex={0}
                rowClassName={({ saldado }) => {
                  let className = '';
                  if (saldado) {
                    className += 'txtdec-line-throught disabled';
                  }
                  return className;
                }}
                pageSize={convenio?.numero_de_pagos + 1}
              />
            </Col>
          </>
        )}
        {visibleCobroForm && !visibleCancel && !visibleCancelParcialidades && (
          <FormDatosDePago
            data={datosDePago}
            setData={setDatosDePago}
            total={form.getFieldValue('abono')}
            onPayment={onPayment}
            disableImporte
            setNetPayLoading={setLoadingNetPay}
            setVisibleGetRecibos={setVisibleGetRecibos}
            isConvenio
            onNetPayConvenio={payWithNetPayConvenio}
            padron={padron}
            parcialidades={selectedRowKeys}
            convenio={convenio.id}
          />
        )}
        {visibleCancel && !visibleCancelParcialidades && (
          <Row>
            <Col span={2}>
              <Typography.Title level={4}>
                <InfoCircleOutlined
                  className="warning"
                  style={{
                    fontSize: 24,
                    marginRight: 10,
                  }}
                />
              </Typography.Title>
            </Col>
            <Col span={22}>
              <Typography.Title level={4}>
                {`¿Está seguro de que desea cancelar el convenio con folio: ${convenio?.folio || convenio?.id}?`}
              </Typography.Title>
              <Text>
                Al cancelar el convenio, el padrón deberá pagar el adeudo total
                {' '}
                <strong>
                  {currencyFormatter(convenio?.adeudo)}
                </strong>
                {' '}
                en una sola exhibición.
              </Text>
            </Col>
            <Form
              layout="vertical"
              form={formCancel}
              onFinish={handleCancelConvenio}
              style={{ width: '100%' }}
              initialValues={{ puede_devolver_convenio: false }}
            >
              {permission?.cancelar_convenio_y_abonos && (
                <Col span={24}>
                  <Form.Item
                    label="Hacer devolución de abonos"
                    name="puede_devolver_convenio"
                    rules={[{ required: true }]}
                  >
                    <Select trueFalse />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item
                  name="observaciones_de_cancelacion"
                  label="Observaciones"
                  rules={[{ required: true }]}
                >
                  <Input.TextArea
                    allowClear
                    autoSize={{ minRows: 3, maxRows: 5 }}
                  />
                </Form.Item>
              </Col>
            </Form>
          </Row>
        )}
        {visibleCancelParcialidades && (
          <TableParcialidadesSaldadas
            data={data.filter((item) => item.saldado)}
            callback={handleCancelParcialidades}
            form={formCancelParcialidades}
          />
        )}
        {base64 && visiblePreview && (
          <div style={{ height: '80vh' }}>
            <Previewer
              base64={base64}
              modal={false}
              visible
            />
          </div>
        )}
      </Spin>
    </Modal>
  );
};

const Tag = styled(AntTag)`
  text-decoration: none !important;
`;

// Componente para renderizar mensualidad
const MensualidadRenderer = ({ value, record }) => {
  if (value === 0) {
    return (
      <Tag color="blue" style={{ margin: 0 }}>
        Anticipo
      </Tag>
    );
  }

  // Agregar indicador visual si está saldado
  if (record?.saldado) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{value}</span>
        <Tag color="success" size="small">
          ✓
        </Tag>
      </div>
    );
  }

  return <span>{value}</span>;
};

MensualidadRenderer.propTypes = {
  value: propTypes.number.isRequired,
  record: propTypes.shape({
    saldado: propTypes.bool,
  }),
};

MensualidadRenderer.defaultProps = {
  record: {},
};

// Componente para renderizar fechas
const FechaRenderer = ({ value, showTooltip = false }) => {
  if (!value) {
    return <span style={{ color: '#999' }}>-</span>;
  }

  const formattedDate = value.format(DATE_FRONTEND_FORMAT);

  if (showTooltip) {
    return (
      <Tooltip title={`Fecha completa: ${value.format('DD/MM/YYYY HH:mm:ss')}`}>
        <span>{formattedDate}</span>
      </Tooltip>
    );
  }

  return <span>{formattedDate}</span>;
};

FechaRenderer.propTypes = {
  value: propTypes.oneOfType([propTypes.object, propTypes.instanceOf(Moment)]),
  showTooltip: propTypes.bool,
};

FechaRenderer.defaultProps = {
  value: null,
  showTooltip: false,
};

// Componente especializado para fechas de vencimiento
const FechaVencimientoRenderer = ({ value, record }) => {
  if (!value) {
    return <span style={{ color: '#999' }}>-</span>;
  }

  const today = Moment();
  const vencimiento = Moment(value);
  const diasDiferencia = vencimiento.diff(today, 'days');

  let color = 'default';
  let statusText = '';

  if (record.saldado) {
    color = 'success';
    statusText = ' (Pagado)';
  } else if (diasDiferencia < 0) {
    color = 'error';
    statusText = ` (Vencido hace ${Math.abs(diasDiferencia)} días)`;
  } else if (diasDiferencia <= 3) {
    color = 'warning';
    statusText = ` (Vence en ${diasDiferencia} días)`;
  }

  const formattedDate = value.format(DATE_FRONTEND_FORMAT);

  return (
    <Tooltip title={statusText}>
      <Tag color={color} style={{ margin: 0 }}>
        {formattedDate}
      </Tag>
    </Tooltip>
  );
};

FechaVencimientoRenderer.propTypes = {
  value: propTypes.oneOfType([propTypes.object, propTypes.instanceOf(Moment)]),
  record: propTypes.shape({
    saldado: propTypes.bool,
  }).isRequired,
};

FechaVencimientoRenderer.defaultProps = {
  value: null,
};

// Componente para mostrar importe con descuentos
const ImporteConDescuento = ({ record, value }) => {
  // Trabajar con centavos: record.importe_original y
  //  record.importe_de_la_parcialidad ya están en centavos
  const diff = record.importe_original - record.importe_de_la_parcialidad;
  return (
    <>
      <Tag color="blue" style={{ margin: 0 }}>
        <Tooltip title="Importe original">
          {currencyFormatter(fromCents(record.importe_original))}
        </Tooltip>
      </Tag>
      {' - '}
      <Tag color="red" style={{ margin: 0 }}>
        <Tooltip title="Importe abonado">
          {currencyFormatter(fromCents(diff))}
        </Tooltip>
      </Tag>
      {' = '}
      {currencyFormatter(fromCents(value))}
    </>
  );
};

ImporteConDescuento.propTypes = {
  record: propTypes.shape({
    importe_original: propTypes.number,
    importe_de_la_parcialidad: propTypes.number,
  }).isRequired,
  value: propTypes.number.isRequired,
};

// Componente para mostrar importe saldado
const ImporteSaldado = ({ record }) => (
  <>
    <Tag color="success">
      Abonado:
      {' '}
      {currencyFormatter(record.total_abonado)}
    </Tag>
    {' '}
    <span>
      {currencyFormatter(0)}
    </span>
  </>
);

ImporteSaldado.propTypes = {
  record: propTypes.shape({
    total_abonado: propTypes.number,
  }).isRequired,
};

// Componente para mostrar importe con sobrante
const ImporteConSobrante = ({ record, value }) => {
  // Trabajar con centavos y luego convertir para mostrar
  const _sobrante = record.sobrante;
  const _result = value - _sobrante;

  if (_result === 0) {
    return currencyFormatter(fromCents(value));
  }

  return (
    <>
      {currencyFormatter(fromCents(value))}
      <Tooltip title="Abono a la próxima mensualidad con el sobrante.">
        <span className="danger">
          {' - '}
          {currencyFormatter(fromCents(_sobrante))}
        </span>
      </Tooltip>
      <span className="success">
        {' = '}
        {currencyFormatter(fromCents(_result))}
      </span>
    </>
  );
};

ImporteConSobrante.propTypes = {
  record: propTypes.shape({
    sobrante: propTypes.number,
  }).isRequired,
  value: propTypes.number.isRequired,
};

// Componente para mostrar importe con abono parcial
const ImporteConAbonoParcial = ({ record, value }) => (
  <>
    {(record?.total_abonado > 0 && !record?.saldado) && (
      <Tag color="success">
        Abonado:
        {' '}
        {currencyFormatter(record.total_abonado)}
      </Tag>
    )}
    <Tooltip title={`Importe original (${currencyFormatter(fromCents(record.importe_original))})`}>
      {currencyFormatter(fromCents(value))}
    </Tooltip>
  </>
);

ImporteConAbonoParcial.propTypes = {
  record: propTypes.shape({
    total_abonado: propTypes.number,
    saldado: propTypes.bool,
    importe_original: propTypes.number,
  }).isRequired,
  value: propTypes.number.isRequired,
};

// Componente principal para renderizar importe
const ImporteRenderer = ({ value, record }) => {
  const _value = (value * 100) / 100;

  // Caso 1: Importe con descuento (importe original diferente al de la parcialidad)
  if (record?.importe_original !== record?.importe_de_la_parcialidad) {
    return <ImporteConDescuento record={record} value={_value} />;
  }

  // Caso 2: Parcialidad saldada
  if (record.saldado) {
    return <ImporteSaldado record={record} />;
  }

  // Caso 3: Importe con sobrante
  if (record?.sobrante) {
    return <ImporteConSobrante record={record} value={_value} />;
  }

  // Caso 4: Importe normal con abono parcial
  return <ImporteConAbonoParcial record={record} value={_value} />;
};

ImporteRenderer.propTypes = {
  value: propTypes.number.isRequired,
  record: propTypes.shape({
    importe_original: propTypes.number,
    importe_de_la_parcialidad: propTypes.number,
    saldado: propTypes.bool,
    sobrante: propTypes.number,
  }).isRequired,
};

ModalCobroParcialidades.propTypes = {
  callback: propTypes.func.isRequired,
  permission: propTypes.shape({
    cancelar_convenio: propTypes.bool,
    cancelar_convenio_y_abonos: propTypes.bool,
    cobrar: propTypes.bool,
    pagar_en_cortes_anteriores: propTypes.bool,
  }).isRequired,
};

export default ModalCobroParcialidades;
